/**
 * Sample Ratio Mismatch (SRM) の検定。
 *
 * 50:50 で振り分けたつもりの実験で実際の人数比が偏っている場合、
 * 割り当て・計測・重複除外のどこかが壊れている可能性が高い。
 * この状態の p 値や信頼区間は、統計以前の理由で信用できない。
 */

import { chiSquareSf } from './chisquare'

export interface SrmResult {
  chiSquare: number
  df: number
  pValue: number
  /** 実際の構成比 */
  observedShares: number[]
  /** 期待した構成比 */
  expectedShares: number[]
}

/**
 * 観測人数が期待した割付比から乖離していないかをカイ二乗適合度検定で調べる。
 *
 * @param observed 各群の人数
 * @param weights 期待する割付比（合計が 1 でなくてよい。省略時は均等割付）
 */
export function detectSampleRatioMismatch(
  observed: readonly number[],
  weights?: readonly number[],
): SrmResult | null {
  const k = observed.length
  if (k < 2) return null

  const total = observed.reduce((sum, n) => sum + n, 0)
  if (total <= 0) return null

  const rawWeights = weights ?? observed.map(() => 1)
  if (rawWeights.length !== k) return null
  const weightTotal = rawWeights.reduce((sum, w) => sum + w, 0)
  if (weightTotal <= 0) return null

  const expectedShares = rawWeights.map((w) => w / weightTotal)

  let chiSquare = 0
  for (let i = 0; i < k; i++) {
    const expected = total * expectedShares[i]
    if (expected <= 0) return null
    const diff = observed[i] - expected
    chiSquare += (diff * diff) / expected
  }

  const df = k - 1
  return {
    chiSquare,
    df,
    pValue: chiSquareSf(chiSquare, df),
    observedShares: observed.map((n) => n / total),
    expectedShares,
  }
}

/** SRM を疑うべき閾値。通常の有意水準より厳しくとる慣行に合わせている。 */
export const SRM_ALERT_THRESHOLD = 0.001
